/* eslint-disable camelcase */
import { RoutalPalette } from '@nx-smartmonkey/ui';
import React from 'react';

import { PlannerCredentials } from '../../../../domain/credentials/PlannerCredentials';
import { Driver } from '../../../../domain/driver/Driver';
import { GeoFence } from '../../../../domain/geoFence/GeoFence';
import { Project } from '../../../../domain/project/Project';
import { Stop } from '../../../../domain/stop/Stop';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import Text from '../../../../react-components/Text';
import { useRetrieveActivePlan } from '../hooks/plan-details/useRetrieveActivePlan';

/**
 * @description Validates if stop of a driver has optimization warnings.
 * @param {*} driver
 */
export const driverStopsHasAnyWarnings = ({
  driver,
  planGeoFences,
  activeProject,
  userCredentials,
}: {
  driver?: Driver;
  planGeoFences: Array<GeoFence>;
  activeProject?: Project;
  userCredentials?: PlannerCredentials;
}): boolean => {
  return driver?.stops
    ? driver.stops.some((stop) =>
        hasAnyWarning({ stop, planGeoFences, planDrivers: [driver], activeProject, userCredentials })
      )
    : false;
};

/**
 * @description Validates if any stop has optimization warnings.
 * @param {*} plan
 */
export const anyStopHasAnyWarnings = ({
  stops,
  drivers,
  planGeoFences,
  activeProject,
  userCredentials,
}: {
  stops: Array<Stop>;
  drivers: Array<Driver>;
  planGeoFences: Array<GeoFence>;
  activeProject?: Project;
  userCredentials?: PlannerCredentials;
}): boolean => {
  return stops.some((stop) =>
    hasAnyWarning({ stop, planGeoFences, planDrivers: drivers, activeProject, userCredentials })
  );
};

/**
 * @description Validates if there are any stop optimization warnings.
 * @param {*} stop
 * @param {*} planDrivers
 */
export const hasAnyWarning = ({
  stop,
  planGeoFences,
  planDrivers,
  activeProject,
  userCredentials,
}: {
  stop: Stop;
  planGeoFences: Array<GeoFence>;
  planDrivers: Driver[];
  activeProject?: Project;
  userCredentials?: PlannerCredentials;
}): boolean => {
  const optimizationWarnings = getOptimizationWarnings({
    stop,
    planGeoFences,
    planDrivers,
    activeProject,
    userCredentials,
  });
  const optimizationArray = Object.keys(optimizationWarnings).map((key: string) => optimizationWarnings[key]);
  return optimizationArray.some((item: boolean) => item === true);
};

/**
 * @description Validates if there are any stop optimization warnings.
 * @param {*} stop
 * @param {*} planDrivers
 */
const getOptimizationWarnings = ({
  stop,
  planGeoFences,
  planDrivers,
  activeProject,
  userCredentials,
}: {
  stop: Stop;
  planGeoFences: Array<GeoFence>;
  planDrivers: Driver[];
  activeProject?: Project;
  userCredentials?: PlannerCredentials;
}): Record<string, any> => {
  return {
    short_timewindows: isInvalidDuration({ stop, activeProject }),
    weight_exceeds: isInvalidWeight({ stop, planDrivers, activeProject }),
    volume_exceeds: isInvalidVolume({ stop, planDrivers, activeProject }),
    no_time_widows_match: isInvalidStopTimewindows({ stop, planDrivers, activeProject }),
    no_time_widows_and_duration_match: isInvalidStopTimewindowsAndDuration({ stop, planDrivers, activeProject }),
    pickup_after_delivery: isInvalidPickup({ stop, activeProject }),
    no_requires_match: isInvalidRequires({ stop, planDrivers, activeProject }),
    no_address: !stop.location?.isValid(),
    pickup_no_address: activeProject?.constraints.pickups && stop.pickup && !stop.pickup.location?.isValid(),
    no_geo_fences: isInvalidGeofence({ stop, planGeoFences, planDrivers, activeProject, userCredentials }),
  };
};

/**
 * @description Validates if the stop time_windows are shorter than the duration.
 * @param {*} stop
 */
const isInvalidDuration = ({ stop, activeProject }: { stop: Stop; activeProject?: Project }): boolean => {
  if (!activeProject?.constraints.time_windows) return false;

  if (Boolean(stop?.time_windows?.length) && Boolean(stop?.duration)) {
    return !stop?.time_windows?.some(
      (time_window: any) => !stop.duration || time_window[1] - time_window[0] > stop.duration
    );
  }
  return false;
};

/**
 * @description Validates if there are vehicles with weight capacity that fits the stop weight.
 * @param {*} stop
 * @param {*} planDrivers
 */
const isInvalidWeight = ({
  stop,
  planDrivers,
  activeProject,
}: {
  stop: Stop;
  planDrivers: Driver[];
  activeProject?: Project;
}): boolean => {
  if (!activeProject?.constraints.weight) return false;

  if (!stop.weight) {
    return false;
  }

  if (planDrivers.length === 0) {
    return false;
  }

  // Checking if any driver satisfies the stop weight restriction.
  const anyValid = planDrivers.some((driver: any) => {
    if (!driver.max_weight) return false;
    if (stop.weight) {
      return driver.max_weight >= stop.weight;
    }
    return true;
  });
  return !anyValid;
};

/**
 * @description Validates if there are vehicles with volume capacity that fits the stop volume.
 * @param {*} stop
 * @param {*} planDrivers
 */
const isInvalidVolume = ({
  stop,
  planDrivers,
  activeProject,
}: {
  stop: Stop;
  planDrivers: Driver[];
  activeProject?: Project;
}): boolean => {
  if (!activeProject?.constraints.volume) return false;

  if (!stop.volume) {
    return false;
  }

  if (planDrivers.length === 0) {
    return false;
  }

  // Checking if any driver satisfies the stop volume restriction.
  const anyValid = planDrivers.some((driver: any) => {
    if (!driver.max_volume) return false;
    if (stop.volume) {
      return driver.max_volume >= stop.volume;
    }
    return true;
  });
  return !anyValid;
};

/**
 * @description Validates if there are vehicles with time_windows that match the stop time_windows.
 * @param {*} stop
 * @param {*} planDrivers
 * @param {*} activeProject
 */
const isInvalidStopTimewindows = ({
  stop,
  planDrivers,
  activeProject,
}: {
  stop: Stop;
  planDrivers: Driver[];
  activeProject?: Project;
}): boolean => {
  if (!activeProject?.constraints.time_windows) return false;

  if (planDrivers.length === 0) {
    return false;
  }

  if ((stop.time_windows ?? []).length === 0) return false;
  return !planDrivers.some((driver: Driver) => {
    if (!driver.time_window) return true;
    return stop.time_windows!.some((stopTimeWindow: any) => isValidTimeWindow(driver.time_window!, stopTimeWindow));
  });
};

/**
 * Valid cases:
 *  - Driver timewindow is inside the stop time window.
 *  - Stop timewindow is inside the driver time window.
 *  - Stop time window start is between the driver timewindow. And the stop timewindow end is before the driver timewindow end.
 *  - Stop time window end is between the driver timewindow. And the stop timewindow start is before the driver timewindow start.
 */
const isValidTimeWindow = (driverTimeWindow: [number, number], stopTimeWindow: [number, number]) => {
  if (
    // Driver timewindow is inside the stop time window.
    (driverTimeWindow[0] >= stopTimeWindow[0] && driverTimeWindow[1] <= stopTimeWindow[1]) ||
    // Stop timewindow is inside the driver time window.
    (driverTimeWindow[0] <= stopTimeWindow[0] && driverTimeWindow[1] >= stopTimeWindow[1]) ||
    // Stop time window start is between the driver timewindow. And the stop timewindow end is before the driver timewindow end.
    (driverTimeWindow[0] <= stopTimeWindow[0] &&
      driverTimeWindow[1] > stopTimeWindow[0] &&
      driverTimeWindow[1] <= stopTimeWindow[1]) ||
    // Stop time window end is between the driver timewindow. And the stop timewindow start is before the driver timewindow start.
    (driverTimeWindow[0] > stopTimeWindow[0] &&
      driverTimeWindow[0] < stopTimeWindow[1] &&
      driverTimeWindow[1] > stopTimeWindow[1])
  ) {
    return true;
  }

  // Other cases
  return false;
};

/**
 * @description Validates if there are vehicles with time_windows that match the stop time_windows and has enough time due the stop duration.
 * @param {*} stop
 * @param {*} planDrivers
 * @param {*} activeProject
 */
const isInvalidStopTimewindowsAndDuration = ({
  stop,
  planDrivers,
  activeProject,
}: {
  stop: Stop;
  planDrivers: Driver[];
  activeProject?: Project;
}): boolean => {
  if (!activeProject?.constraints.time_windows) return false;

  if (planDrivers.length === 0) {
    return false;
  }

  if (stop.time_windows?.length === 0) return false;
  return !planDrivers.some((route: Driver) => {
    if (!route.time_window) return true;
    return (
      stop.time_windows &&
      stop.time_windows.some((stopTimeWindow: any) => {
        if (route.time_window !== undefined && stop.duration !== undefined) {
          const startOverlap = stopTimeWindow[0] <= route.time_window[0] ? route.time_window[0] : stopTimeWindow[0];
          const endOverlap = stopTimeWindow[1] <= route.time_window[1] ? stopTimeWindow[1] : route.time_window[1];
          return stop.duration <= endOverlap - startOverlap;
        }
        return true;
      })
    );
  });
};

/**
 * @description Validates if the stop pickup is after the stop delivery.
 * Invalid if there is not any pickup time_window earlier or equal to any delivery time_window.
 * @param {*} stop
 */
const isInvalidPickup = ({ stop, activeProject }: { stop: Stop; activeProject?: Project }): boolean => {
  if (!activeProject?.constraints.pickups) return false;

  if (!stop.pickup || !stop.pickup.time_windows || stop.pickup.time_windows.length === 0) {
    return false;
  }

  const isValid = stop.pickup.time_windows.some((pickupTimewindow: any) => {
    if (!stop.time_windows || stop.time_windows.length === 0) {
      return true;
    }

    return stop.time_windows.some((deliveryTimewindow: any) => {
      return pickupTimewindow[0] < deliveryTimewindow[1];
    });
  });

  return !isValid;
};

/**
 * @description Validates if there are vehicles with provides that match the stop requires.
 * @param {*} stop
 * @param {*} planDrivers
 */
const isInvalidRequires = ({
  stop,
  planDrivers,
  activeProject,
}: {
  stop: Stop;
  planDrivers: Driver[];
  activeProject?: Project;
}): boolean => {
  if (!activeProject?.constraints.provide_requires) return false;

  if (planDrivers.length === 0) {
    return false;
  }

  return (
    stop.requires !== undefined &&
    !planDrivers.some((driver: Driver) => {
      if (!driver.provides) return false;
      return !stop?.requires?.some((r: any) => !driver?.provides?.includes(r));
    })
  );
};

/**
 * @description Validates if there are vehicles with the geo fence where the stop lives in.
 * @param {*} stop
 * @param {*} planGeoFences
 * @param {*} planDrivers
 * @param {*} activeProject
 * @param {*} userCredentials
 */
const isInvalidGeofence = ({
  stop,
  planGeoFences,
  planDrivers,
  activeProject,
  userCredentials,
}: {
  stop: Stop;
  planGeoFences: Array<GeoFence>;
  planDrivers: Driver[];
  activeProject?: Project;
  userCredentials?: PlannerCredentials;
}): boolean => {
  if (!userCredentials?.subscription?.test(`planner.geofences`)) return false;

  if (!activeProject?.geo_fences_enabled) return false;

  if (!stop.location?.lat || !stop.location?.lng) {
    return false;
  }

  if (planDrivers.length === 0) {
    return false;
  }

  const geoFenceFound = planGeoFences
    .filter((p) => p.enabled)
    .find((planGF) => {
      return planGF.isMarkerInsideGeoFence({ lat: stop.location!.lat!, lng: stop.location!.lng! });
    });
  if (!geoFenceFound) return false;

  return !planDrivers.some((driver: Driver) => driver.geo_fences?.includes(geoFenceFound.id));
};

type OptimizationWarningsContentProps = {
  stop: Stop;
  planDrivers: Driver[];
};

export const OptimizationWarningsContent = ({
  stop,
  planDrivers,
}: React.PropsWithChildren<OptimizationWarningsContentProps>) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: activePlan } = useRetrieveActivePlan();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();

  if (
    !activePlan ||
    !hasAnyWarning({ stop, planGeoFences: projectGeoFences ?? [], planDrivers, activeProject, userCredentials })
  )
    return null;

  const optimizationWarnings = getOptimizationWarnings({
    stop,
    planGeoFences: projectGeoFences ?? [],
    planDrivers,
    activeProject,
    userCredentials,
  });

  return (
    <div style={{ margin: `10px`, fontSize: `14px`, lineHeight: `1.6em` }}>
      <Text tagName="p" intlId="services.optimizationwarnings.info" style={{ color: RoutalPalette.oldWhite }} />

      {Object.keys(optimizationWarnings).map((constraintKey: string) => {
        if (optimizationWarnings[constraintKey]) {
          return (
            <p style={{ marginLeft: `8px` }} key={constraintKey}>
              <Text tagName="span" style={{ color: RoutalPalette.oldWhite }}>
                -{` `}
              </Text>
              <Text
                tagName="span"
                intlId={`services.optimizationwarnings.${constraintKey}`}
                style={{ color: RoutalPalette.oldWhite }}
              />
            </p>
          );
        }
        return null;
      })}
    </div>
  );
};
